import { useCallback, useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import UnderlineExt from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, ChevronDown, ChevronUp, FileText, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import NoteEditorToolbar from "./NoteEditorToolbar";

interface ProgrammeNotesCardProps {
  leadId: string;
  initialNotes: string | null | undefined;
  readOnly?: boolean;
}

const EMPTY = "";

export function ProgrammeNotesCard({ leadId, initialNotes, readOnly }: ProgrammeNotesCardProps) {
  const initial = initialNotes ?? EMPTY;
  const [expanded, setExpanded] = useState<boolean>(!!initial && initial.trim().length > 0);
  const [status, setStatus] = useState<"idle" | "dirty" | "saving" | "saved" | "error">("idle");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skipNextUpdate = useRef(false);
  const lastSavedRef = useRef<string>(initial);

  const editor = useEditor({
    extensions: [
      StarterKit,
      UnderlineExt,
      Placeholder.configure({ placeholder: "Add notes that should appear at the top of generated programmes and quotes..." }),
    ],
    content: initial,
    editable: !readOnly,
    onUpdate: ({ editor: ed }) => {
      if (skipNextUpdate.current) {
        skipNextUpdate.current = false;
        return;
      }
      const html = ed.getHTML();
      // Treat empty editor output as empty string for storage.
      const value = html === "<p></p>" ? EMPTY : html;
      setStatus("dirty");
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => save(value), 800);
    },
  });

  // Sync external initialNotes once when it arrives (e.g. async lead fetch).
  useEffect(() => {
    if (!editor) return;
    const incoming = initialNotes ?? EMPTY;
    if (incoming !== lastSavedRef.current && incoming !== editor.getHTML()) {
      skipNextUpdate.current = true;
      editor.commands.setContent(incoming || "");
      lastSavedRef.current = incoming;
    }
  }, [initialNotes, editor]);

  const save = useCallback(async (rawHtml: string) => {
    const sanitized = rawHtml ? DOMPurify.sanitize(rawHtml, { USE_PROFILES: { html: true } }) : null;
    setStatus("saving");
    const { error } = await supabase
      .from("leads")
      .update({ programme_notes: sanitized })
      .eq("id", leadId);
    if (error) {
      setStatus("error");
      toast.error("Could not save programme notes");
      return;
    }
    lastSavedRef.current = sanitized ?? EMPTY;
    setStatus("saved");
    setTimeout(() => setStatus((s) => (s === "saved" ? "idle" : s)), 1500);
  }, [leadId]);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="flex items-center gap-2 text-left"
        >
          <FileText className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold">Programme Notes</h3>
          <span className="text-xs text-muted-foreground">
            (shown at top of generated programmes &amp; quotes)
          </span>
        </button>
        <div className="flex items-center gap-2">
          <div className="text-xs text-muted-foreground flex items-center gap-1 min-w-[70px] justify-end">
            {status === "saving" && (<><Loader2 className="h-3 w-3 animate-spin" /> Saving…</>)}
            {status === "saved" && (<><Check className="h-3 w-3 text-primary" /> Saved</>)}
            {status === "dirty" && <span>Unsaved…</span>}
            {status === "error" && <span className="text-destructive">Save failed</span>}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setExpanded((e) => !e)}
            aria-label={expanded ? "Collapse" : "Expand"}
          >
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="mt-3 rounded-md border">
          <NoteEditorToolbar
            editor={editor}
            onImageUpload={async () => { toast.message("Images aren't supported in programme notes."); }}
            onFileAttach={async () => { toast.message("Attachments aren't supported in programme notes."); }}
          />
          <div className="p-3">
            <EditorContent editor={editor} className="prose-editor min-h-[120px]" />
          </div>
        </div>
      )}
    </Card>
  );
}

export default ProgrammeNotesCard;